import { printTemplates } from '../api/endpoints';
import { getStoredUser } from './auth';
import { documentFor } from './printDocuments';
import { renderLayout } from './printLayouts';
import type { ChurchConfig, DocumentType, PrintTemplate } from '../types';

/**
 * The one way this application puts a document on paper.
 *
 * A screen hands over the module it belongs to, the kind of document it is,
 * and the record's own values. This resolves the layout the parish configured
 * under Settings, fills its {{placeholders}}, and hands a standalone document
 * to the browser's print dialog without leaving the page.
 *
 * The printed page is built from HTML and CSS and never from the application's
 * screen: no canvas, no image capture, no copy of the React tree. What comes
 * out carries real, selectable text and real tables, so "Save as PDF" in the
 * browser's own print dialog produces a proper PDF rather than a picture.
 */

/** Values a template can reference. Nested objects and lists are supported. */
export type PrintData = Record<string, unknown>;

export interface PrintRequest {
  /** Module id exactly as the registry spells it, e.g. 'Baptism'. */
  module: string;
  documentType: DocumentType;
  /** The record's fields, as the template's {{placeholders}} name them. */
  data: PrintData;
  /** Print with this template instead of the configured default. */
  template?: PrintTemplate;
  /** The parish's identity, for the {{church*}} placeholders. */
  config?: ChurchConfig | null;
  /** Window title; also the file name the browser suggests for a PDF. */
  title?: string;
}

export class NoTemplateError extends Error {
  constructor(public module: string, public documentType: DocumentType) {
    super(
      `No active print template is configured for ${module} — ${documentType}. `
      + 'Please configure one in Settings › Print Templates.'
    );
    this.name = 'NoTemplateError';
  }
}

const PAGE_MM: Record<string, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  A5: { w: 148, h: 210 },
  LETTER: { w: 216, h: 279 },
  LEGAL: { w: 216, h: 356 }
};

const ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
};

/**
 * Every filled value is escaped.
 *
 * A template is written by an administrator, but the values poured into it are
 * ordinary records — a member called `<script>` must print as those characters
 * and never run. Escaping at the substitution boundary means the template's own
 * markup still works while nothing a user typed can become markup.
 */
const escapeValue = (value: unknown): string =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ESCAPES[c]);

/** `a.b.c` down a nested object; undefined when any step is missing. */
const reach = (data: PrintData, path: string): unknown =>
  path.split('.').reduce<unknown>(
    (acc, key) => (acc && typeof acc === 'object' ? (acc as PrintData)[key] : undefined),
    data
  );

/**
 * `{{#rows}} … {{/rows}}` repeats its body once per item, with the item's own
 * fields in scope — how a register prints its lines without the module writing
 * a table. Sections resolve before plain placeholders so a row's fields are
 * filled from the row, not the document.
 */
const fillSections = (html: string, data: PrintData): string =>
  html.replace(
    /\{\{([#^])\s*([\w.]+)\s*\}\}([\s\S]*?)\{\{\/\s*\2\s*\}\}/g,
    (_whole, sigil: string, path: string, body: string) => {
      const rows = reach(data, path);
      const empty = Array.isArray(rows) ? rows.length === 0 : !rows;

      // `{{^name}}` is the other half of the condition — what to print when
      // there is nothing. It is how a parish with no logo gets its name set in
      // type instead of a broken-image icon.
      if (sigil === '^') {
        return empty ? fillPlaceholders(fillSections(body, data), data) : '';
      }
      // A section over a plain value is a condition, not a repetition.
      if (!Array.isArray(rows)) {
        return empty ? '' : fillPlaceholders(fillSections(body, data), data);
      }
      return rows
        .map((row, index) => {
          const scope: PrintData = {
            ...data,
            ...(row && typeof row === 'object' ? (row as PrintData) : { value: row }),
            index: index + 1
          };
          return fillPlaceholders(fillSections(body, scope), scope);
        })
        .join('');
    }
  );

/**
 * A placeholder with no value prints as an empty string rather than leaving
 * `{{name}}` on the paper or throwing: one missing field must not cost the
 * whole document.
 */
const fillPlaceholders = (html: string, data: PrintData): string =>
  html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_whole, path: string) => {
    const value = reach(data, path);
    return value === undefined || value === null ? '' : escapeValue(value);
  });

/** Placeholders every document can use, whatever module it came from. */
const ambientData = (config?: ChurchConfig | null): PrintData => {
  const now = new Date();
  return {
    churchName: config?.churchName || '',
    churchAddress: config?.address || '',
    churchContactNo: config?.contactNo || '',
    churchLogo: config?.logoBase64 || '',
    // A template is a shape any parish can wear, so its colour comes from the
    // parish's own brand colour rather than being written into the markup.
    themeColor: config?.themeColor || '',
    currentDate: now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    currentTime: now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    generatedBy: (getStoredUser()?.name || '').trim()
  };
};

/** The template's own values, filled — used by the editor's preview too. */
export const renderTemplate = (
  template: PrintTemplate,
  data: PrintData,
  config?: ChurchConfig | null
): { header: string; body: string; footer: string } => {
  const merged: PrintData = { ...ambientData(config), ...data };
  const fill = (html?: string) => fillPlaceholders(fillSections(html || '', merged), merged);
  return {
    header: fill(template.headerHtml),
    body: fill(template.htmlContent),
    footer: fill(template.footerHtml)
  };
};

/**
 * The print stylesheet.
 *
 * `@page` states the sheet and its margins, so the browser lays the document
 * out to the template's page size rather than to whatever the last print used.
 * The rest keeps long documents readable across pages: a table's head repeats,
 * rows are not split, and the footer stays with the content it belongs to.
 */
const documentCss = (template: PrintTemplate): string => {
  const size = PAGE_MM[template.pageSize || 'A4'] || PAGE_MM.A4;
  const landscape = template.orientation === 'LANDSCAPE';
  const width = landscape ? size.h : size.w;
  const m = {
    top: template.marginTop ?? 15,
    right: template.marginRight ?? 15,
    bottom: template.marginBottom ?? 15,
    left: template.marginLeft ?? 15
  };

  return `
    @page {
      size: ${template.pageSize || 'A4'} ${landscape ? 'landscape' : 'portrait'};
      margin: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm;
    }

    html, body { margin: 0; padding: 0; background: #FFF; color: #000; }

    body {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 12px;
      line-height: 1.5;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* On screen the document sits on a sheet so the preview matches the paper. */
    @media screen {
      body { background: #E9E9EC; padding: 20px; }
      .doc {
        width: ${width}mm;
        margin: 0 auto;
        background: #FFF;
        padding: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm;
        box-shadow: 0 4px 18px rgba(0,0,0,0.18);
      }
    }
    @media print {
      .doc { width: auto; margin: 0; padding: 0; box-shadow: none; }
      .no-print { display: none !important; }
    }

    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 6px 8px; text-align: left; vertical-align: top; }

    /* A long register runs over pages: repeat the head, never split a row. */
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr, .avoid-break { break-inside: avoid; page-break-inside: avoid; }

    h1, h2, h3 { break-after: avoid; page-break-after: avoid; margin: 0 0 8px; }
    img { max-width: 100%; }

    .doc-header { margin-bottom: 14px; }
    .doc-footer { margin-top: 18px; }
  `;
};

/** A complete standalone document — never a copy of the application's screen. */
export const buildPrintHtml = (
  template: PrintTemplate,
  data: PrintData,
  config?: ChurchConfig | null,
  title?: string
): string => {
  const { header, body, footer } = renderTemplate(template, data, config);
  const docTitle = escapeValue(title || template.templateName || 'Document');

  // The charset is declared first and before any text: without it the window
  // decodes this UTF-8 markup as Latin-1 and ₹ and other symbols come out
  // garbled.
  return `<!DOCTYPE html><html><head><meta charset="utf-8">`
    + `<title>${docTitle}</title>`
    + `<style>${documentCss(template)}</style>`
    + `</head><body><div class="doc">`
    + (header ? `<div class="doc-header">${header}</div>` : '')
    + `<div class="doc-body">${body}</div>`
    + (footer ? `<div class="doc-footer">${footer}</div>` : '')
    + `</div></body></html>`;
};

/**
 * The template a request should use: the one named, else the configured
 * default. A working list marked `builtIn` falls back to the standard design
 * instead of refusing, so it prints before anyone has visited Settings.
 */
export const resolveTemplate = async (request: PrintRequest): Promise<PrintTemplate> => {
  if (request.template) return request.template;
  const found = await printTemplates.resolve(request.module, request.documentType);
  if (found && found.id) return found;
  if (documentFor(request.documentType)?.builtIn) {
    return renderLayout('', request.documentType, request.config?.themeColor);
  }
  throw new NoTemplateError(request.module, request.documentType);
};

/**
 * Opens the document in its own window and asks the browser to print it.
 *
 * The same window serves "Save as PDF" — it is the browser's own dialog, so
 * the PDF carries the document's real text and tables.
 */
/** Every image in the document, so the logo is on the page before it prints. */
const imagesReady = (doc: Document): Promise<void> => {
  const pending = Array.from(doc.images).filter((img) => !img.complete);
  if (!pending.length) return Promise.resolve();
  return Promise.all(
    pending.map((img) => new Promise<void>((done) => {
      // A logo that fails to load must not hold the dialog shut.
      img.addEventListener('load', () => done(), { once: true });
      img.addEventListener('error', () => done(), { once: true });
    }))
  ).then(() => undefined);
};

export const printDocument = async (request: PrintRequest): Promise<void> => {
  const template = await resolveTemplate(request);
  const html = buildPrintHtml(template, request.data, request.config, request.title);

  // The document prints from a frame inside this page rather than a second
  // tab: the dialog opens over the screen the user is already on, nothing is
  // blocked by a pop-up blocker, and they are not left with a stray tab to
  // close afterwards.
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.title = 'Printing';
  // Off-screen rather than hidden or zero-sized: the document still needs a
  // real viewport to lay itself out in before the dialog opens.
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;';
  frame.srcdoc = html;

  // Chrome names a saved PDF after the *hosting* page, so the tab is renamed
  // for the length of the dialog and put back afterwards.
  const pageTitle = document.title;
  const docTitle = request.title || template.templateName || 'Document';

  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(guard);
      document.title = pageTitle;
      // Removed on a later tick: Safari cancels the dialog if the frame goes
      // while it is still open.
      window.setTimeout(() => frame.remove(), 1000);
      resolve();
    };

    // Every path out of here has to release the caller. A caller left waiting
    // is not a stalled print — it is a Print button stuck on "Preparing…" and,
    // where the button sits inside a dialog, an overlay that never closes and
    // takes the whole screen with it.
    const guard = window.setTimeout(finish, 20000);

    frame.addEventListener('load', () => {
      const view = frame.contentWindow;
      if (!view) { finish(); return; }

      imagesReady(view.document).then(() => {
        document.title = docTitle;
        view.addEventListener('afterprint', finish, { once: true });
        try {
          view.focus();
          view.print();
        } catch {
          /* the browser refused the dialog; finish() below still runs */
        }
        // `print()` returns once the dialog has been dealt with, printed or
        // cancelled, so the caller is released here. Waiting on `afterprint`
        // alone stranded it: the event does not fire on cancel in every
        // browser, and the button never came back.
        finish();
      }).catch(finish);
    }, { once: true });

    document.body.appendChild(frame);
  });
};

export default printDocument;
